
  import { computed, defineComponent, onMounted, reactive, Ref, toRefs } from "vue";
  import InventoriesTasksForm from "../forms/InventoriesTasksForm.vue";
  import ModalDialog from "@/components/ModalDialog.vue";
  import { Task, TaskForm, TaskRequest } from "@/model/OrderTaskImport";
  import EmployeeName from "@/model/EmployeeName";
  import {helpers, required} from "@vuelidate/validators";
  import { t } from "@/mixin/mixins";
  import useVuelidate from "@vuelidate/core";
  import eventBus from "@/eventbus";
  import { MessageTypes } from "@/components/message-types";
  import OrderTaskService from "@/service/OrderTaskService";
  import { AxiosError } from "axios";

  export default defineComponent({
    name: "InventoriesTasksDialog",
    components: {
      InventoriesTasksForm,
      ModalDialog
    },
    setup() {
      const state = reactive({
        dialog: false,
        edit: false,
        task: {} as TaskForm,
        orders: [],
        oldTaskLabel: "",
        serverErrors: new Map<string, string>()
      });

      const toEmployeeName = (task: Task) => {
        const employee = new EmployeeName();
        employee.uuid = task.employee.id;
        employee.employeeId = task.employeeNumber;
        employee.firstName = task.employee.firstName;
        employee.lastName = task.employee.lastName;
        return employee;
      };

      onMounted(() => {
        eventBus.$on(MessageTypes.OPEN_TASK_DIALOG, (editedTask: Task, orders: []) => {
          state.serverErrors = new Map<string, string>();

          if (editedTask) {
            state.edit = true;
            state.task = {
              id: editedTask.id,
              orderId: editedTask.order.id,
              label: editedTask.label,
              positions: editedTask.positions,
              priority: editedTask.priority,
              employee: toEmployeeName(editedTask)
            };
            state.oldTaskLabel = editedTask.label;
          } else {
            state.edit = false;
            state.task = {priority: 0} as TaskForm;
            state.oldTaskLabel = "";
          }

          if (orders) {
            state.orders = orders;
          }

          state.dialog = true;
        });
      });

      const onTaskChanged = (newTask: TaskForm) => {
        state.task = newTask;
      };

      // The v2 task endpoints answer invalid fields with a problem detail that lists them in errors
      const handleSaveError = (error: AxiosError, errorMessageKey: string) => {
        if (error && error.response && error.response.status === 422) {
          eventBus.$emit(MessageTypes.WARNING, t("checkInput"));
          const errorMap = new Map<string, string>();
          // @ts-ignore
          (error.response.data.errors || []).forEach((error: ValidationError) => {
            errorMap.set(error.path, error.message);
          });
          state.serverErrors = errorMap;
        } else if (!error.response || error.response.status !== 409) {
          // A 409 is already shown by the global error handler
          eventBus.$emit(MessageTypes.ERROR, t(errorMessageKey));
        }
      };

      const save = () => {
        state.serverErrors = new Map<string, string>();
        v$.value.$reset();
        v$.value.$touch();

        if (!v$.value.$invalid) {
          const request: TaskRequest = {
            label: (state.task.label as string).trim(),
            positions: Number(state.task.positions),
            priority: state.task.priority === "" || state.task.priority == null ? null : Number(state.task.priority),
            employeeId: state.task.employee?.uuid as string
          };

          if (!state.edit) {
            OrderTaskService.addTask(state.task.orderId as string, request)
              .then(response => {
                const newTask = response.data;
                eventBus.$emit(MessageTypes.TASK_CREATED, newTask);
                closeDialog();
                eventBus.$emit(MessageTypes.SUCCESS, t("inventories.tasks.message.success.create"), 1000);
              })
              .catch((error: AxiosError) => handleSaveError(error, "inventories.tasks.message.error.create"));
          } else {
            OrderTaskService.updateTask(state.task.id as string, request)
              .then(response => {
                const newTask = response.data;
                eventBus.$emit(MessageTypes.TASK_CREATED, newTask);
                closeDialog();
              })
              .catch((error: AxiosError) => handleSaveError(error, "inventories.tasks.message.error.edit"));
          }
        }
      };

      const closeDialog = () => {
        state.dialog = false;
        state.task = {} as TaskForm;
        state.orders = [];
        state.oldTaskLabel = "";
        state.serverErrors = new Map<string, string>();
        v$.value.$reset();
      };

      const rules = computed(() => ({
        task: {
          orderId: {
            required: helpers.withMessage(t("inventories.tasks.validation.orderId"), required),
          },
          label: {
            required: helpers.withMessage(t("inventories.tasks.validation.label"), required),
          },
          positions: {
            required: helpers.withMessage(t("inventories.tasks.validation.positions"), required),
          },
          employee: {
            required: helpers.withMessage(t("inventories.tasks.validation.employeeId"), required),
          }
        }
      }));

      // @ts-ignore
      const v$ = useVuelidate(rules, state);
      
      const errors = computed(() => {
        const errors = new Map<string, string | Ref<string>>();
        if (state.serverErrors.size > 0) {
          return state.serverErrors;
        } else {
          const model = v$.value.task;
          if (model.$dirty) {
            if(model.orderId.$error) {
              errors.set("orderId", model.orderId.$errors[0].$message);
            }
            if(model.label.$error) {
              errors.set("label", model.label.$errors[0].$message);
            }
            if(model.positions.$error) {
              errors.set("positions", model.positions.$errors[0].$message);
            }
            if(model.employee.$error) {
              errors.set("employeeId", model.employee.$errors[0].$message);
            }
          }
        }
        return errors;
      });

      return {
        ...toRefs(state),
        onTaskChanged,
        save,
        closeDialog,
        errors
      };
    }
  });
